/**
 * State of a SearchableSelect: open / closed, the search text (cleared on
 * every open and close), the options it filters, and picking one (which
 * reports it and closes the list).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SearchableSelectOption } from './SearchableSelect';

export function useSearchableSelect<T>(options: SearchableSelectOption<T>[], onChange: (value: T) => void) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search]);
  const pick = useCallback(
    (option: SearchableSelectOption<T>) => {
      onChange(option.value);
      setSearch('');
      setOpen(false);
    },
    [onChange],
  );
  const openChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  }, []);
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);
  return { open, setOpen, search, setSearch, filtered, pick, openChange };
}
